import { useQuery } from "@apollo/client";
import {
  LiveEventSortableFieldEnum,
  SortDirectionEnum,
  LiveEventOrderingInput,
  LiveEventAudienceEnum,
  LiveEventTypeEnum,
  LiveEventStatusEnum,
  OrganizationLiveEventsQuery,
  OrganizationLiveEventsQueryVariables,
  LiveEvent,
} from "@/__generated__/types";
import { ORGANIZATION_LIVE_EVENTS_QUERY } from "@/api/queries/orgLiveEvents";

export const DEFAULT_INITIAL_RECORDS_LIVE_EVENTS = 10;

export interface LiveEventsFilters {
  audience?: LiveEventAudienceEnum[];
  eventType?: LiveEventTypeEnum[];
  chatChannel?: string[];
  createdBy?: string[];
}

export interface UseLiveEventsParams {
  orgId?: string;
  status: LiveEventStatusEnum[];
  first?: number;
  last?: number;
  after?: string | null;
  before?: string | null;
  filters?: LiveEventsFilters;
  ordering?: LiveEventOrderingInput[];
}

const extractEdges = (
  data: OrganizationLiveEventsQuery | undefined,
): LiveEvent[] => {
  if (!data?.node?.__typename || data.node.__typename !== "Organization")
    return [];
  return (
    data.node.liveEvents?.edges
      ?.filter(
        (edge): edge is { cursor: string; node: LiveEvent } =>
          edge?.node !== null,
      )
      .map((edge) => edge.node)
      .filter((node) => !!node) || []
  );
};

export const useLiveEvents = (variables: UseLiveEventsParams) => {
  const defaultOrdering: LiveEventOrderingInput[] = [
    {
      field: LiveEventSortableFieldEnum.StartTime,
      direction: SortDirectionEnum.Asc,
    },
  ];

  const { data, loading, refetch } = useQuery<
    OrganizationLiveEventsQuery,
    OrganizationLiveEventsQueryVariables
  >(ORGANIZATION_LIVE_EVENTS_QUERY, {
    variables: {
      orgId: variables.orgId || "",
      status: variables.status,
      first: variables.first,
      last: variables.last,
      after: variables.after,
      before: variables.before,
      eventType: variables.filters?.eventType,
      audience: variables.filters?.audience,
      chatChannel: variables.filters?.chatChannel,
      createdBy: variables.filters?.createdBy,
      ordering: variables.ordering || defaultOrdering,
    },
    skip: !variables.orgId,
  });

  const liveEvents = extractEdges(data);

  return {
    liveEventsData: liveEvents,
    totalCount:
      data?.node?.__typename === "Organization"
        ? data.node.liveEvents?.totalCount || 0
        : 0,
    hasPreviousPage:
      data?.node?.__typename === "Organization"
        ? data.node.liveEvents?.pageInfo?.hasPreviousPage || false
        : false,
    hasNextPage:
      data?.node?.__typename === "Organization"
        ? data.node.liveEvents?.pageInfo?.hasNextPage || false
        : false,
    startCursor:
      data?.node?.__typename === "Organization"
        ? data.node.liveEvents?.pageInfo?.startCursor || null
        : null,
    endCursor:
      data?.node?.__typename === "Organization"
        ? data.node.liveEvents?.pageInfo?.endCursor || null
        : null,
    cursor:
      data?.node?.__typename === "Organization"
        ? data.node.liveEvents?.pageInfo?.endCursor || null
        : null,
    loading,
    refetch,
  };
};
